import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { usePermissions } from "@/contexts/PermissionContext";
import { useVisibleRefresh } from "@/hooks/useVisibleRefresh";
import { toast } from "sonner";
import {
  AlertTriangle,
  Archive,
  Blend,
  BookOpen,
  CalendarClock,
  CalendarDays,
  CircleCheck,
  CircleX,
  Clock3,
  Eye,
  GraduationCap,
  MapPin,
  MonitorPlay,
  MoreHorizontal,
  Pencil,
  Plus,
  Presentation,
  Radio,
  Search,
  X,
} from "lucide-react";
import { norm } from "@/components/RelationSelects";
import DoxaSelect from "@/components/doxa/DoxaSelect";
import { EmptyState, PageHeader } from "@/components/doxa/AdminSurface";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const STATUS_META = {
  SCHEDULED: {
    label: "Agendada",
    icon: CalendarClock,
    className: "border-[#E7DFD3] bg-[#F6F3EE] text-[#5F574D]",
  },
  OPEN: {
    label: "Aberta",
    icon: Radio,
    className: "border-[#CFE8D7] bg-[#EAF6EE] text-[#2F855A]",
  },
  CLOSED: {
    label: "Encerrada",
    icon: CircleCheck,
    className: "border-[#DED8CF] bg-[#F3F0EB] text-[#6B655E]",
  },
  CANCELLED: {
    label: "Cancelada",
    icon: CircleX,
    className: "border-[#F3CACA] bg-[#FFF1F1] text-[#C53030]",
  },
  ARCHIVED: {
    label: "Arquivada",
    icon: Archive,
    className: "border-[#DED8CF] bg-[#F3F0EB] text-[#6B655E]",
  },
};

const TYPE_META = {
  PRESENTIAL: {
    label: "Presencial",
    icon: Presentation,
    className: "border-[#E7DFD3] bg-[#F6F3EE] text-[#5F574D]",
  },
  ONLINE: {
    label: "Online",
    icon: MonitorPlay,
    className: "border-[#D6E9DF] bg-[#EEF8F2] text-[#2F855A]",
  },
  HYBRID: {
    label: "Híbrida",
    icon: Blend,
    className: "border-[#F3D9A7] bg-[#FFF7E8] text-[#9A6518]",
  },
};

const STATUS_OPTS = [
  { value: "current", label: "Ativas / próximas" },
  { value: "closed", label: "Encerradas" },
  { value: "cancelled", label: "Canceladas" },
  { value: "archived", label: "Arquivadas" },
  { value: "all", label: "Todas" },
];
const LESSON_TYPE_OPTIONS = [
  { value: "PRESENTIAL", label: "Presencial" },
  { value: "HYBRID", label: "Híbrida" },
  { value: "ONLINE", label: "Online" },
];
const MONTHS = ["JAN", "FEV", "MAR", "ABR", "MAI", "JUN", "JUL", "AGO", "SET", "OUT", "NOV", "DEZ"];
const INPUT_CLASS = "w-full rounded-xl border border-[#E0D9CD] bg-white px-3 py-2.5 text-sm text-[#1F1F1F] outline-none transition placeholder:text-[#A59C90] hover:border-[#CFC5B7] focus:border-[#F75A1B] focus:ring-2 focus:ring-[#F75A1B]/15";

function dateParts(value) {
  const [year, month, day] = String(value || "").split("-");
  if (!year || !month || !day) return { full: "Sem data", day: "—", month: "DATA" };
  return {
    full: `${day}/${month}/${year}`,
    day,
    month: MONTHS[Math.max(0, Number(month) - 1)] || month,
  };
}

function lessonSortKey(lesson) {
  return `${lesson.lesson_date || "9999-12-31"}T${lesson.start_time || "23:59"}`;
}

function timeRange(start, end) {
  if (start && end) return `${start}–${end}`;
  return start || end || "Horário não definido";
}

function SemanticChip({ meta, testId }) {
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-[11px] font-semibold ${meta.className}`}
      data-testid={testId}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      {meta.label}
    </span>
  );
}

function LessonRowMenu({ lesson, canManage, onOpen, onEdit }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          onClick={(event) => event.stopPropagation()}
          onPointerDown={(event) => event.stopPropagation()}
          className="grid h-9 w-9 shrink-0 place-items-center rounded-xl border border-transparent text-[#8A8378] transition hover:border-[#F75A1B]/20 hover:bg-[#FFF0E9] hover:text-[#F75A1B] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F75A1B]/25 data-[state=open]:border-[#F75A1B]/20 data-[state=open]:bg-[#FFF0E9] data-[state=open]:text-[#F75A1B]"
          aria-label={`Ações da aula ${lesson.title}`}
          data-testid={`lesson-row-menu-${lesson.id}`}
        >
          <MoreHorizontal className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={7} className="w-60 rounded-xl border-[#E0D9CD] bg-white p-1.5 shadow-[0_14px_40px_rgba(45,31,20,.14)]">
        <DropdownMenuLabel className="px-2.5 pb-1 pt-2">
          <span className="block text-[10px] font-semibold uppercase tracking-[0.12em] text-[#A59C90]">Ações da aula</span>
          <span className="mt-0.5 block truncate text-xs font-semibold normal-case tracking-normal text-[#4A4640]">{lesson.title}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-[#EBE5DB]" />
        <DropdownMenuItem onSelect={onOpen} className="cursor-pointer rounded-lg px-2.5 py-2.5 focus:bg-[#FFF0E9] focus:text-[#D94A12]">
          <Eye className="h-4 w-4" /> Abrir detalhes
        </DropdownMenuItem>
        {canManage && (
          <DropdownMenuItem onSelect={onEdit} className="cursor-pointer rounded-lg px-2.5 py-2.5 focus:bg-[#FFF0E9] focus:text-[#D94A12]" data-testid={`edit-lesson-row-${lesson.id}`}>
            <Pencil className="h-4 w-4" /> Editar aula
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Lessons() {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canManage = can("lesson.manage");
  const [lessons, setLessons] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [editingLesson, setEditingLesson] = useState(null);
  const [classGroups, setClassGroups] = useState([]);
  const [modules, setModules] = useState([]);
  const [fClass, setFClass] = useState("");
  const [fModule, setFModule] = useState("");
  const [fStatus, setFStatus] = useState("current");
  const [fLesson, setFLesson] = useState("");
  const [q, setQ] = useState("");

  const load = () => {
    const backendStatus = fStatus === "current" || fStatus === "closed" ? "active" : fStatus;
    const params = { status_filter: backendStatus };
    if (fClass) params.class_group_id = fClass;
    if (fModule) params.module_id = fModule;
    return api.get("/lessons", { params }).then(({ data }) => setLessons(data));
  };

  useEffect(() => { void load(); }, [fClass, fModule, fStatus]);
  useVisibleRefresh(() => load(), { intervalMs: 0 });
  useEffect(() => { api.get("/class-groups").then(({ data }) => setClassGroups(data)); }, []);
  useEffect(() => {
    if (fClass) api.get("/modules", { params: { class_group_id: fClass } }).then(({ data }) => setModules(data));
    else setModules([]);
    setFModule("");
    setFLesson("");
  }, [fClass]);

  const cgMap = Object.fromEntries(classGroups.map((group) => [group.id, group.name]));
  const moduleMap = Object.fromEntries(modules.map((module) => [module.id, module.name]));
  const statusScoped = lessons.filter((lesson) => {
    if (fStatus === "current") return lesson.status === "SCHEDULED" || lesson.status === "OPEN";
    if (fStatus === "closed") return lesson.status === "CLOSED";
    return true;
  });
  const ordered = [...statusScoped].sort((a, b) => lessonSortKey(a).localeCompare(lessonSortKey(b)));
  const lessonOptions = [
    { value: "", label: "Todas as datas" },
    ...ordered.map((lesson) => ({ value: lesson.id, label: `${dateParts(lesson.lesson_date).full} — ${lesson.title}` })),
  ];
  const filtered = ordered.filter((lesson) => {
    if (fLesson && lesson.id !== fLesson) return false;
    if (!q) return true;
    const hay = norm([
      lesson.title,
      lesson.location,
      cgMap[lesson.class_group_id],
      moduleMap[lesson.module_id],
      STATUS_META[lesson.status]?.label,
      TYPE_META[lesson.lesson_type]?.label,
      dateParts(lesson.lesson_date).full,
    ].join(" "));
    return hay.includes(norm(q));
  });

  const statusMeta = fStatus === "current" ? "ativas ou agendadas" : STATUS_OPTS.find((option) => option.value === fStatus)?.label?.toLowerCase() || "na visão atual";

  const openLesson = (lessonId) => navigate(`/lessons/${lessonId}`);

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-4 sm:p-5 md:p-6" data-testid="lessons-page">
      <PageHeader
        eyebrow="Operação acadêmica"
        title="Aulas"
        description="Organize a agenda, acompanhe o andamento e entre rapidamente na operação de cada aula."
        meta={`${filtered.length} aula(s) ${statusMeta}`}
        action={canManage ? (
          <button onClick={() => setOpenCreate(true)} className="doxa-btn-primary inline-flex items-center gap-2 px-4 py-2.5" data-testid="new-lesson-btn">
            <Plus className="h-4 w-4" /> Nova aula
          </button>
        ) : null}
      />

      <section className="rounded-[16px] border border-[#E0D9CD] bg-white p-3 sm:p-4" data-testid="lessons-filters">
        <div className="grid gap-2.5 sm:grid-cols-2 xl:grid-cols-[1fr_1fr_180px_1.55fr]">
          <DoxaSelect
            value={fClass}
            onValueChange={(value) => { setFClass(value); setFLesson(""); }}
            options={[{ value: "", label: "Todas as turmas" }, ...classGroups.map((group) => ({ value: group.id, label: group.name }))]}
            className="w-full"
            testId="filter-class"
            ariaLabel="Filtrar por turma"
          />
          <DoxaSelect
            value={fModule}
            onValueChange={(value) => { setFModule(value); setFLesson(""); }}
            options={[{ value: "", label: "Todos os módulos" }, ...modules.map((module) => ({ value: module.id, label: module.name }))]}
            disabled={!fClass}
            className="w-full"
            testId="filter-module"
            ariaLabel="Filtrar por módulo"
          />
          <DoxaSelect
            value={fStatus}
            onValueChange={(value) => { setFStatus(value); setFLesson(""); }}
            options={STATUS_OPTS}
            className="w-full"
            testId="filter-status"
            ariaLabel="Filtrar por status"
          />
          <DoxaSelect
            value={fLesson}
            onValueChange={setFLesson}
            options={lessonOptions}
            disabled={ordered.length === 0}
            className="w-full"
            contentClassName="min-w-[320px]"
            testId="filter-lesson-date"
            ariaLabel="Filtrar por data e aula"
          />
        </div>

        <div className="relative mt-3">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8A8378]" />
          <input
            value={q}
            onChange={(event) => setQ(event.target.value)}
            placeholder="Buscar por aula, turma, módulo, local ou data…"
            className="h-11 w-full rounded-xl border border-[#E0D9CD] bg-[#FFFEFC] pl-9 pr-9 text-sm outline-none transition placeholder:text-[#A59C90] hover:border-[#CFC5B7] focus:border-[#F75A1B] focus:ring-2 focus:ring-[#F75A1B]/15"
            data-testid="lessons-search"
          />
          {q && (
            <button
              type="button"
              onClick={() => setQ("")}
              className="absolute right-2 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded-lg text-[#8A8378] hover:bg-[#FFF0E9] hover:text-[#F75A1B]"
              aria-label="Limpar busca"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </section>

      {filtered.length > 0 ? (
        <div className="space-y-2.5" data-testid="lessons-list">
          {filtered.map((lesson) => {
            const status = STATUS_META[lesson.status] || STATUS_META.SCHEDULED;
            const modality = TYPE_META[lesson.lesson_type] || TYPE_META.PRESENTIAL;
            const date = dateParts(lesson.lesson_date);
            return (
              <div
                key={lesson.id}
                role="link"
                tabIndex={0}
                onClick={() => openLesson(lesson.id)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    openLesson(lesson.id);
                  }
                }}
                className="group cursor-pointer rounded-[16px] border border-[#E0D9CD] bg-white p-3.5 transition hover:border-[#F75A1B]/30 hover:shadow-[0_10px_30px_rgba(68,53,35,0.05)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F75A1B]/25 sm:p-4"
                data-testid={`lesson-row-${lesson.id}`}
                aria-label={`Abrir ${lesson.title}`}
              >
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="grid h-[62px] w-[62px] shrink-0 place-items-center rounded-[14px] bg-[#FFF0E9] text-center">
                    <div>
                      <p className="font-display text-xl font-bold leading-none text-[#D94A12]">{date.day}</p>
                      <p className="mt-1 text-[10px] font-semibold tracking-[0.12em] text-[#A54A24]">{date.month}</p>
                    </div>
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0">
                        <p className="break-words font-display text-base font-bold text-[#1F1F1F] transition group-hover:text-[#D94A12] sm:text-lg">{lesson.title}</p>
                        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[#6B655E]">
                          <span className="inline-flex items-center gap-1.5"><GraduationCap className="h-3.5 w-3.5 text-[#F75A1B]" /> {cgMap[lesson.class_group_id] || "Turma"}</span>
                          <span className="inline-flex items-center gap-1.5"><Clock3 className="h-3.5 w-3.5" /> {timeRange(lesson.start_time, lesson.end_time)}</span>
                          {moduleMap[lesson.module_id] && <span className="inline-flex items-center gap-1.5"><BookOpen className="h-3.5 w-3.5" /> {moduleMap[lesson.module_id]}</span>}
                          {lesson.location && <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {lesson.location}</span>}
                        </div>
                      </div>
                      <div className="flex shrink-0 flex-wrap items-center gap-1.5 sm:justify-end">
                        <SemanticChip meta={status} testId={`lesson-status-${lesson.id}`} />
                        <SemanticChip meta={modality} testId={`lesson-modality-${lesson.id}`} />
                        <LessonRowMenu
                          lesson={lesson}
                          canManage={canManage}
                          onOpen={() => openLesson(lesson.id)}
                          onEdit={() => setEditingLesson(lesson)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <section className="rounded-[18px] border border-[#E0D9CD] bg-white px-5 py-3">
          <EmptyState
            icon={CalendarDays}
            title={statusScoped.length === 0 ? (fStatus === "current" ? "Nenhuma aula ativa ou agendada" : "Nenhuma aula nesta categoria") : "Nenhuma aula corresponde aos filtros"}
            description={statusScoped.length === 0 && fStatus === "current" ? "As aulas encerradas continuam preservadas no filtro Encerradas." : "Ajuste turma, módulo, status, data ou busca para ampliar a visão."}
          />
          {canManage && statusScoped.length === 0 && fStatus === "current" && (
            <div className="pb-5 text-center">
              <button type="button" onClick={() => setOpenCreate(true)} className="doxa-btn-primary inline-flex items-center gap-2 px-4 py-2" data-testid="empty-new-lesson-btn">
                <Plus className="h-4 w-4" /> Criar aula
              </button>
            </div>
          )}
        </section>
      )}

      {openCreate && <LessonFormModal classGroups={classGroups} onClose={() => setOpenCreate(false)} onSaved={load} />}
      {editingLesson && (
        <LessonFormModal
          lesson={editingLesson}
          classGroups={classGroups}
          onClose={() => setEditingLesson(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}

export function LessonFormModal({ lesson, classGroups, onClose, onSaved }) {
  const isEdit = !!lesson;
  const [form, setForm] = useState({
    class_group_id: lesson?.class_group_id || classGroups[0]?.id || "",
    module_id: lesson?.module_id || "",
    title: lesson?.title || "",
    description: lesson?.description || "",
    lesson_date: lesson?.lesson_date || "",
    start_time: lesson?.start_time || "19:30",
    end_time: lesson?.end_time || "21:30",
    lesson_type: lesson?.lesson_type || classGroups.find((group) => group.id === (lesson?.class_group_id || classGroups[0]?.id))?.default_lesson_type || "PRESENTIAL",
    location: lesson?.location || "",
    online_link: lesson?.online_link || "",
    video_url: lesson?.video_url || "",
  });
  const [modules, setModules] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (form.class_group_id) api.get("/modules", { params: { class_group_id: form.class_group_id } }).then(({ data }) => setModules(data));
    else setModules([]);
  }, [form.class_group_id]);

  const group = classGroups.find((item) => item.id === form.class_group_id);
  const noPeriod = group && (!group.start_date || !group.end_date);

  const submit = async (event) => {
    event.preventDefault();
    if (!form.class_group_id) return toast.error("Selecione a turma.");
    setBusy(true);
    try {
      const payload = { ...form, module_id: form.module_id || null };
      if (isEdit) await api.put(`/lessons/${lesson.id}`, payload);
      else await api.post("/lessons", payload);
      toast.success(isEdit ? "Aula atualizada!" : "Aula criada!");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-[#1F1712]/45 p-4 backdrop-blur-[2px]" onClick={onClose}>
      <div className="max-h-[90vh] w-full max-w-xl overflow-hidden rounded-[22px] border border-white/70 bg-white shadow-[0_24px_80px_rgba(45,31,20,.18)]" onClick={(event) => event.stopPropagation()} data-testid="lesson-modal">
        <div className="flex items-start justify-between gap-4 border-b border-[#EBE5DB] px-5 py-5">
          <div className="flex items-start gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#FFF0E9] text-[#F75A1B]"><CalendarDays className="h-4 w-4" /></span>
            <div>
              <h2 className="font-display text-lg font-bold">{isEdit ? "Editar aula" : "Nova aula"}</h2>
              <p className="mt-1 text-xs text-[#6B655E]">{isEdit ? "Atualize agenda, vínculo e formato sem alterar o histórico operacional." : "Defina vínculo, agenda e formato da nova aula."}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="grid h-9 w-9 place-items-center rounded-lg text-[#8A8378] hover:bg-[#F6F3EE]" aria-label="Fechar">
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={submit} className="flex max-h-[calc(90vh-92px)] flex-col">
          <div className="space-y-4 overflow-y-auto px-5 py-5">
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-[#4A4640]">Título</label>
              <input required value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} className={INPUT_CLASS} data-testid="lesson-title" />
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-[#4A4640]">Turma</label>
                <DoxaSelect
                  value={form.class_group_id}
                  onValueChange={(classGroupId) => {
                    const selected = classGroups.find((item) => item.id === classGroupId);
                    setForm({ ...form, class_group_id: classGroupId, module_id: "", ...(isEdit ? {} : { lesson_type: selected?.default_lesson_type || "PRESENTIAL" }) });
                  }}
                  options={[{ value: "", label: "Selecione…" }, ...classGroups.map((item) => ({ value: item.id, label: item.name }))]}
                  className="w-full"
                  testId="lesson-class"
                  ariaLabel="Turma da aula"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-[#4A4640]">Módulo</label>
                <DoxaSelect
                  value={form.module_id || ""}
                  onValueChange={(moduleId) => setForm({ ...form, module_id: moduleId })}
                  options={[{ value: "", label: "— Nenhum —" }, ...modules.map((module) => ({ value: module.id, label: module.name }))]}
                  disabled={!form.class_group_id}
                  className="w-full"
                  testId="lesson-module"
                  ariaLabel="Módulo da aula"
                />
              </div>
            </div>

            {noPeriod && (
              <div className="inline-flex items-start gap-2 rounded-xl border border-[#F3D9A7] bg-[#FFF7E8] p-3 text-xs leading-relaxed text-[#8A5A14]" data-testid="lesson-no-period-warning">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                Esta turma não tem período configurado. Recomendamos definir início/fim da turma para limitar as datas das aulas.
              </div>
            )}

            <div className="rounded-[14px] border border-[#EBE5DB] bg-[#FAF7F1] p-3.5">
              <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#A59C90]">Agenda</p>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                <div className="col-span-2 sm:col-span-1">
                  <label className="mb-1.5 block text-xs font-semibold text-[#4A4640]">Data</label>
                  <input type="date" required value={form.lesson_date} min={group?.start_date || undefined} max={group?.end_date || undefined}
                    onChange={(event) => setForm({ ...form, lesson_date: event.target.value })} className={INPUT_CLASS} data-testid="lesson-date" />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-[#4A4640]">Início</label>
                  <input type="time" value={form.start_time} onChange={(event) => setForm({ ...form, start_time: event.target.value })} className={INPUT_CLASS} />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-[#4A4640]">Fim</label>
                  <input type="time" value={form.end_time} onChange={(event) => setForm({ ...form, end_time: event.target.value })} className={INPUT_CLASS} />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-[#4A4640]">Modalidade</label>
                <DoxaSelect value={form.lesson_type} onValueChange={(lessonType) => setForm({ ...form, lesson_type: lessonType })} options={LESSON_TYPE_OPTIONS} className="w-full" testId="lesson-type" ariaLabel="Tipo da aula" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-[#4A4640]">Local</label>
                <input value={form.location} onChange={(event) => setForm({ ...form, location: event.target.value })} className={INPUT_CLASS} placeholder="Ex.: Auditório Doxa" />
              </div>
            </div>

            {(form.lesson_type === "ONLINE" || form.lesson_type === "HYBRID") && (
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-[#4A4640]">Link da aula online</label>
                <input value={form.online_link} onChange={(event) => setForm({ ...form, online_link: event.target.value })} placeholder="https://…" className={INPUT_CLASS} data-testid="lesson-online-link" />
              </div>
            )}
            {(form.lesson_type === "ONLINE" || form.lesson_type === "HYBRID") && (
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-[#4A4640]">Vídeo da aula (YouTube)</label>
                <input value={form.video_url} onChange={(event) => setForm({ ...form, video_url: event.target.value })} placeholder="https://www.youtube.com/watch?v=…" className={INPUT_CLASS} data-testid="lesson-video-url" />
                <p className="mt-1.5 text-[11px] text-[#8A8378]">O aluno assiste ao vídeo no portal quando a regra de status permitir.</p>
              </div>
            )}
          </div>

          <div className="flex flex-col-reverse gap-2 border-t border-[#EBE5DB] bg-[#FAF7F1] px-5 py-4 sm:flex-row sm:justify-end">
            <button type="button" onClick={onClose} disabled={busy} className="h-10 rounded-xl border border-[#E0D9CD] bg-white px-4 text-sm font-semibold text-[#4A4640] hover:bg-[#F6F3EE] disabled:opacity-50">Cancelar</button>
            <button type="submit" disabled={busy} className="doxa-btn-primary h-10 px-4" data-testid="lesson-submit">
              {busy ? "Salvando…" : isEdit ? "Salvar alterações" : "Criar aula"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
